import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import type { Ayah } from '@/types/quran';
import { Button } from '@/components/ui/button';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { ArrowLeft, Share2 } from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { toPng, toBlob } from 'html-to-image';
import { getTodayOneDayOneAyah } from '@/services/oneDayOneAyah.service';
import { getSurahs } from '@/services/quran.service';
import { getHijriToday, type HijriTodayResponse } from '@/services/hijri.service';
import toast from 'react-hot-toast';

type OneDayLanguage = 'th' | 'en';

interface LocationState {
  ayah?: Ayah;
  meaningLanguage?: OneDayLanguage;
  showArabic?: boolean;
}

function buildShareText(
  ayah: Ayah,
  options: { showArabic: boolean; meaningLanguage: OneDayLanguage },
): string {
  const arabic = options.showArabic ? ayah.text : '';
  const translation =
    ayah.translations && ayah.translations.length > 0
      ? ayah.translations[0].text
      : '';
  const refText = `(${ayah.surahNumber}:${ayah.numberInSurah})`;

  const parts: string[] = [];
  if (arabic) parts.push(arabic);
  if (translation) parts.push(translation);
  parts.push(refText);
  parts.push('— ramadanlifecenter.com');

  return parts.filter(Boolean).join('\n\n');
}

export function removeAyahNumber(text: string): string {
  if (!text) return '';
  return text.replace(/[\u06DD\u0660-\u0669\u06F0-\u06F9\uFD3E\uFD3F]/g, '').trim();
}

export function OneDayOneAyahSharePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [api, setApi] = useState<any>();
  const [currentSlide, setCurrentSlide] = useState(0);

  const state = (location.state ?? {}) as LocationState;

  const [ayah, setAyah] = useState<Ayah | null>(state.ayah ?? null);
  const [meaningLanguage, setMeaningLanguage] = useState<OneDayLanguage>(
    state.meaningLanguage ?? 'th',
  );
  const [showArabic, setShowArabic] = useState<boolean>(
    state.showArabic ?? true,
  );
  const [fontSize, setFontSize] = useState<number>(20);
  const [translationFontSize, setTranslationFontSize] = useState<number>(14);
  const [surahName, setSurahName] = useState<string>('');
  const [hijriDate, setHijriDate] = useState<HijriTodayResponse | null>(null);
  const [isCopyingImage, setIsCopyingImage] = useState(false);

  useEffect(() => {
    if (!api) return;

    setCurrentSlide(api.selectedScrollSnap());

    api.on('select', () => {
      setCurrentSlide(api.selectedScrollSnap());
    });
  }, [api]);

  useEffect(() => {
    let mounted = true;
    getHijriToday().then((date) => {
      if (mounted) setHijriDate(date);
    });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!ayah) {
      const stored = getTodayOneDayOneAyah();
      if (stored && stored.ayah) {
        setAyah(stored.ayah);
        setMeaningLanguage(stored.meaningLanguage);
        setShowArabic(stored.showArabic);
        
        if (stored.ayah.surahNumber) {
          getSurahs().then((surahs) => {
            const s = surahs.find(s => s.number === stored.ayah.surahNumber);
            if (s) setSurahName(s.name);
          });
        }
      } else {
        navigate('/dashboard', { replace: true });
      }
    } else if (ayah.surahNumber) {
      getSurahs().then((surahs) => {
        const s = surahs.find(s => s.number === ayah.surahNumber);
        if (s) setSurahName(s.name);
      });
    }
  }, [ayah, navigate]);

  const handleCopy = async () => {
    if (!ayah) return;
    const text = buildShareText(ayah, { showArabic, meaningLanguage });
    try {
      await navigator.clipboard.writeText(text);
      toast.success('คัดลอกข้อความแล้ว', { position: 'top-center' });
    } catch {
      toast.error('คัดลอกไม่สำเร็จ', { position: 'top-center' });
    }
  };

  const handleCopyImage = async () => {
    if (!previewRef.current) return;
    setIsCopyingImage(true);
    try {
      await document.fonts.ready;
      const blobPromise = toBlob(previewRef.current, {
        cacheBust: true,
        pixelRatio: 2,
      }).then((blob) => {
        if (!blob) throw new Error('No blob generated');
        return blob;
      });

      await navigator.clipboard.write([
        new ClipboardItem({
          'image/png': blobPromise,
        }),
      ]);
      toast.success('คัดลอกรูปภาพแล้ว', { position: 'top-center' });
    } catch (error) {
      console.error('Failed to copy image:', error);
      toast.error('คัดลอกรูปภาพไม่สำเร็จ', { position: 'top-center' });
    } finally {
      setIsCopyingImage(false);
    }
  };

  const handleWebShare = async () => {
    if (!ayah) return;

    const text = buildShareText(ayah, { showArabic, meaningLanguage });

    // พยายามแชร์เป็นรูปภาพก่อน ถ้า browser รองรับไฟล์
    const navAny = navigator as any;
    try {
      if (
        previewRef.current &&
        typeof navAny.canShare === 'function' &&
        navAny.canShare({ files: [new File([], 'dummy.png', { type: 'image/png' })] })
      ) {
        await document.fonts.ready;
        const dataUrl = await toPng(previewRef.current, {
          cacheBust: true,
          pixelRatio: 2,
        });
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        const file = new File([blob], 'one-day-one-ayah.png', {
          type: 'image/png',
        });

        if (navAny.canShare({ files: [file] })) {
          await navAny.share({
            title: 'One Day One Ayah',
            files: [file],
          });
          return;
        }
      }
    } catch {
      // ถ้า generate รูปหรือ share ล้มเหลว จะ fallback ไปแชร์ข้อความด้านล่าง
    }

    // Fallback: แชร์เป็นข้อความ หรือคัดลอก
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'One Day One Ayah',
          text,
        });
        return;
      } catch {
        // ผู้ใช้ยกเลิก – จะลองคัดลอกข้อความแทน
      }
    }

    await handleCopy();
  };

  if (!ayah) {
    return null;
  }

  return (
    <div className="h-[100dvh] flex bg-transparent overflow-hidden relative">
      {/* Preview carousel — centered card */}
      <div className="absolute inset-0 flex items-center justify-center px-4">
        <div className="flex flex-col items-center w-full" style={{ maxWidth: 'min(360px, calc((100dvh - 80px) * 9 / 16))' }}>
          <Carousel
            setApi={setApi}
            ref={previewRef as React.RefObject<HTMLDivElement>}
            className="w-full"
            opts={{ loop: false }}
          >
            <CarouselContent>
              {/* Classic */}
              <CarouselItem>
                <div className="aspect-[9/16] w-full rounded-3xl overflow-hidden shadow-lg border bg-white text-black flex items-center justify-center p-6">
                <div className="flex flex-col items-center justify-center text-center gap-4 w-full h-full">
                  {hijriDate && (
                    <div className="flex items-center gap-2 mb-2 px-3 py-1.5 bg-black/5 rounded-full text-black">
                      <span className="text-2xl font-bold font-serif leading-none">{hijriDate.day}</span>
                      <div className="flex flex-col items-start justify-center">
                        <span className="text-[10px] font-bold tracking-wider uppercase leading-none mb-1">{hijriDate.monthName}</span>
                        <span className="text-[9px] opacity-70 leading-none">{hijriDate.year} AH</span>
                      </div>
                    </div>
                  )}
                  {showArabic && (
                    <div
                      className="font-amiri-quran leading-relaxed"
                      style={{ fontSize: `${fontSize}px`, lineHeight: 2 }}
                      dir="rtl"
                    >
                      {ayah.text ? (
                        <span dangerouslySetInnerHTML={{ __html: removeAyahNumber(ayah.text) }} />
                      ) : (
                        removeAyahNumber(ayah.text)
                      )}
                    </div>
                  )}
                  {ayah.translations && ayah.translations.length > 0 && (
                    <p className="leading-relaxed max-w-xs" style={{ fontSize: `${translationFontSize}px` }}>
                      {ayah.translations[0].text}
                    </p>
                  )}
                  <div className="flex flex-col items-center gap-1">
                    <p className="text-[11px] text-muted-foreground">
                      {surahName ? `${surahName} (${ayah.surahNumber}:${ayah.numberInSurah})` : `${ayah.surahNumber}:${ayah.numberInSurah}`} · ramadanlifecenter.com
                    </p>
                  </div>
                </div>
              </div>
              </CarouselItem>

              {/* Transparent / overlay */}
              <CarouselItem>
                <div className="aspect-[9/16] w-full rounded-3xl overflow-hidden shadow-lg bg-transparent text-white flex items-center justify-center p-6">
                <div className="relative z-10 flex flex-col items-center justify-center text-center gap-4 w-full h-full px-4">
                  <div className="bg-black/40 rounded-3xl w-full px-4 py-5 space-y-3 border border-white/10 flex flex-col items-center">
                    {hijriDate && (
                      <div className="flex items-center gap-2 mb-2 px-3 py-1.5 bg-white/10 rounded-full text-white">
                        <span className="text-2xl font-bold font-serif leading-none">{hijriDate.day}</span>
                        <div className="flex flex-col items-start justify-center">
                          <span className="text-[10px] font-bold tracking-wider uppercase leading-none mb-1">{hijriDate.monthName}</span>
                          <span className="text-[9px] opacity-70 leading-none">{hijriDate.year} AH</span>
                        </div>
                      </div>
                    )}
                    {showArabic && (
                      <div
                        className="font-amiri-quran leading-relaxed text-white w-full text-center"
                        style={{ fontSize: `${fontSize}px`, lineHeight: 2 }}
                        dir="rtl"
                      >
                        {ayah.text ? (
                          <span dangerouslySetInnerHTML={{ __html: removeAyahNumber(ayah.text) }} />
                        ) : (
                          removeAyahNumber(ayah.text)
                        )}
                      </div>
                    )}
                    {ayah.translations && ayah.translations.length > 0 && (
                      <p className="leading-relaxed text-slate-100 w-full text-center" style={{ fontSize: `${translationFontSize}px` }}>
                        {ayah.translations[0].text}
                      </p>
                    )}
                    <div className="flex flex-col items-center gap-1 pt-1">
                      <p className="text-[11px] text-slate-300">
                        {surahName ? `${surahName} (${ayah.surahNumber}:${ayah.numberInSurah})` : `${ayah.surahNumber}:${ayah.numberInSurah}`} · ramadanlifecenter.com
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              </CarouselItem>

              {/* Light Glassmorphism */}
              <CarouselItem>
                <div className="aspect-[9/16] w-full rounded-3xl overflow-hidden bg-transparent flex items-center justify-center p-6">
                <div className="relative z-10 flex flex-col items-center justify-center text-center gap-4 w-full h-full px-4">
                  <div className=" bg-white/70 rounded-3xl w-full px-4 py-5 space-y-3 border border-white/40 flex flex-col items-center">
                    {hijriDate && (
                      <div className="flex items-center gap-2 mb-2 px-3 py-1.5 bg-black/5 rounded-full text-slate-900">
                        <span className="text-2xl font-bold font-serif leading-none">{hijriDate.day}</span>
                        <div className="flex flex-col items-start justify-center">
                          <span className="text-[10px] font-bold tracking-wider uppercase leading-none mb-1">{hijriDate.monthName}</span>
                          <span className="text-[9px] opacity-70 leading-none">{hijriDate.year} AH</span>
                        </div>
                      </div>
                    )}
                    {showArabic && (
                      <div
                        className="font-amiri-quran leading-relaxed text-slate-900 w-full text-center"
                        style={{ fontSize: `${fontSize}px`, lineHeight: 2 }}
                        dir="rtl"
                      >
                        {ayah.text ? (
                          <span dangerouslySetInnerHTML={{ __html: removeAyahNumber(ayah.text) }} />
                        ) : (
                          removeAyahNumber(ayah.text)
                        )}
                      </div>
                    )}
                    {ayah.translations && ayah.translations.length > 0 && (
                      <p className="leading-relaxed text-slate-700 w-full text-center" style={{ fontSize: `${translationFontSize}px` }}>
                        {ayah.translations[0].text}
                      </p>
                    )}
                    <div className="flex flex-col items-center gap-1 pt-1">
                      <p className="text-[11px] text-slate-500">
                        {surahName ? `${surahName} (${ayah.surahNumber}:${ayah.numberInSurah})` : `${ayah.surahNumber}:${ayah.numberInSurah}`} · ramadanlifecenter.com
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              </CarouselItem>

              {/* Modern minimal */}
              <CarouselItem>
                <div className="aspect-[9/16] w-full rounded-3xl overflow-hidden bg-linear-to-br from-emerald-50 via-slate-50 to-sky-50 text-slate-900 flex items-center justify-center p-6">
                <div className="relative flex items-center justify-center w-full h-full">
                  <div className="absolute inset-x-8 top-8 flex items-center text-[11px] text-slate-500">
                    <span className="mx-auto">
                      {surahName ? `${surahName} (${ayah.surahNumber}:${ayah.numberInSurah})` : `${ayah.surahNumber}:${ayah.numberInSurah}`}
                    </span>
                  </div>
                  <div className="relative w-full max-w-xs mx-auto mt-6">
                    <div className="rounded-3xl bg-white/80 border border-slate-200/70 px-6 py-6 space-y-4">
                      {hijriDate && (
                        <div className="flex ml-auto items-center gap-1.5 bg-slate-100/80 px-2.5 py-1.5 rounded-lg text-slate-700 shadow-sm border border-slate-200 w-fit mb-2">
                          <span className="text-lg font-bold leading-none">{hijriDate.day}</span>
                          <div className="flex flex-col items-start justify-center">
                            <span className="text-[9px] font-bold uppercase leading-none mb-0.5">{hijriDate.monthName}</span>
                            <span className="text-[8px] opacity-70 leading-none">{hijriDate.year} AH</span>
                          </div>
                        </div>
                      )}
                      {showArabic && (
                        <div
                          className="font-amiri-quran leading-relaxed text-slate-900 text-right"
                          style={{ fontSize: `${fontSize}px`, lineHeight: 2 }}
                          dir="rtl"
                        >
                          {ayah.text ? (
                            <span dangerouslySetInnerHTML={{ __html: removeAyahNumber(ayah.text) }} />
                          ) : (
                            removeAyahNumber(ayah.text)
                          )}
                        </div>
                      )}
                      {ayah.translations && ayah.translations.length > 0 && (
                        <p className="leading-relaxed text-slate-700" style={{ fontSize: `${translationFontSize}px` }}>
                          {ayah.translations[0].text}
                        </p>
                      )}
                    </div>
                    <div className="mt-4 flex items-center justify-center text-[11px] text-slate-500 px-1">
                      <span>ramadanlifecenter.com</span>
                    </div>
                  </div>
                </div>
              </div>
              </CarouselItem>

              {/* Dark Mode + Blue Light Glasses */}
              <CarouselItem>
                <div
                  className="aspect-[9/16] w-full rounded-3xl overflow-hidden flex items-center justify-center relative"
                style={{
                  background: 'linear-gradient(180deg, oklch(0.07 0.02 255) 0%, oklch(0.04 0.01 250) 60%, oklch(0.06 0.025 245) 100%)',
                }}
              >
                <div className="absolute inset-0 pointer-events-none z-0" style={{ background: 'linear-gradient(180deg, oklch(0.80 0.14 65 / 0.07) 0%, oklch(0.70 0.16 50 / 0.12) 100%)' }} />
                <div className="absolute top-0 left-1/2 -translate-x-1/2 pointer-events-none z-0" style={{ width: '120%', height: '75%', background: 'conic-gradient(from 270deg at 50% 0%, transparent 30%, oklch(0.60 0.18 250 / 0.22) 40%, oklch(0.65 0.20 248 / 0.28) 50%, oklch(0.60 0.18 250 / 0.22) 60%, transparent 70%)', filter: 'blur(6px)' }} />
                <div className="absolute top-[15%] left-1/2 -translate-x-1/2 pointer-events-none z-0" style={{ width: '60%', height: '40%', background: 'radial-gradient(ellipse at 50% 0%, oklch(0.65 0.20 250 / 0.20) 0%, transparent 70%)', filter: 'blur(2px)' }} />
                <div className="absolute inset-2 rounded-2xl pointer-events-none z-0" style={{ border: '1px solid oklch(0.50 0.16 250 / 0.20)' }} />

                <div className="relative z-10 flex flex-col items-center justify-center text-center gap-5 w-full h-full px-7 py-8">
                  {hijriDate && (
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: 'oklch(0.55 0.18 250 / 0.15)', border: '1px solid oklch(0.60 0.18 250 / 0.35)', color: 'oklch(0.82 0.10 250)' }}>
                      <span className="text-2xl font-bold font-serif leading-none">{hijriDate.day}</span>
                      <div className="flex flex-col items-start justify-center">
                        <span className="text-[10px] font-bold tracking-wider uppercase leading-none mb-1">{hijriDate.monthName}</span>
                        <span className="text-[9px] opacity-70 leading-none">{hijriDate.year} AH</span>
                      </div>
                    </div>
                  )}
                  {showArabic && (
                    <div className="font-amiri-quran w-full text-center" style={{ fontSize: `${fontSize}px`, lineHeight: 2.2, color: 'oklch(0.93 0.06 250)', filter: 'drop-shadow(0 0 12px oklch(0.65 0.20 250 / 0.55)) drop-shadow(0 0 4px oklch(0.70 0.18 250 / 0.4))' }} dir="rtl">
                      {ayah.text ? (
                        <span dangerouslySetInnerHTML={{ __html: removeAyahNumber(ayah.text) }} />
                      ) : (
                        removeAyahNumber(ayah.text)
                      )}
                    </div>
                  )}
                  {ayah.translations && ayah.translations.length > 0 && (
                    <p className="leading-relaxed w-full text-center" style={{ fontSize: `${translationFontSize}px`, color: 'oklch(0.72 0.08 255)', textShadow: '0 0 20px oklch(0.55 0.14 250 / 0.3)' }}>
                      {ayah.translations[0].text}
                    </p>
                  )}
                  <div className="flex flex-col items-center gap-1 pt-3 w-2/3" style={{ borderTop: '1px solid oklch(0.45 0.12 250 / 0.30)' }}>
                    <p className="text-[11px] font-medium tracking-wide" style={{ color: 'oklch(0.65 0.12 252)' }}>
                      {surahName ? `${surahName} (${ayah.surahNumber}:${ayah.numberInSurah})` : `${ayah.surahNumber}:${ayah.numberInSurah}`}
                    </p>
                    <p className="text-[9px] tracking-widest uppercase font-light mt-0.5" style={{ color: 'oklch(0.45 0.08 252)' }}>ramadanlifecenter.com</p>
                  </div>
                </div>
              </div>
              </CarouselItem>

              {/* Holy Light */}
              <CarouselItem>
                <div className="aspect-[9/16] w-full rounded-3xl overflow-hidden flex items-center justify-center relative" style={{ background: '#000035ff' }}>
                <div className="absolute pointer-events-none z-0" style={{ top: '-10%', left: '-10%', width: '90%', height: '90%', background: 'radial-gradient(ellipse at 0% 0%, oklch(0.98 0.04 90 / 0.55) 0%, oklch(0.92 0.08 70 / 0.25) 30%, oklch(0.80 0.05 60 / 0.08) 55%, transparent 70%)', filter: 'blur(1px)' }} />
                <div className="absolute pointer-events-none z-0" style={{ top: '-5%', left: '-5%', width: '70%', height: '70%', background: 'radial-gradient(ellipse at 0% 0%, oklch(1.0 0.0 0 / 0.12) 0%, transparent 65%)', filter: 'blur(8px)' }} />
                <div className="absolute inset-0 pointer-events-none z-0" style={{ background: 'linear-gradient(160deg, transparent 50%, oklch(0.06 0.01 250 / 0.6) 100%)' }} />

                <div className="relative z-10 flex flex-col items-center justify-center text-center gap-6 w-full h-full px-8 py-10">
                  {hijriDate && (
                    <div className="flex flex-col items-center gap-0.5" style={{ color: 'oklch(0.75 0.04 80)' }}>
                      <span className="text-xs font-light tracking-[0.25em] uppercase">{hijriDate.day} {hijriDate.monthName} {hijriDate.year}</span>
                      <div className="w-6 h-px mt-1" style={{ background: 'oklch(0.75 0.04 80 / 0.5)' }} />
                    </div>
                  )}
                  {showArabic && (
                    <div className="font-amiri-quran w-full text-center" style={{ fontSize: `${fontSize}px`, lineHeight: 2.2, color: 'oklch(0.97 0.01 90)', filter: 'drop-shadow(0 2px 24px oklch(0.95 0.06 80 / 0.35))' }} dir="rtl">
                      {ayah.text ? (
                        <span dangerouslySetInnerHTML={{ __html: removeAyahNumber(ayah.text) }} />
                      ) : (
                        removeAyahNumber(ayah.text)
                      )}
                    </div>
                  )}
                  <div className="w-12 h-px" style={{ background: 'oklch(0.60 0.03 80 / 0.5)' }} />
                  {ayah.translations && ayah.translations.length > 0 && (
                    <p className="leading-relaxed w-full text-center font-light" style={{ fontSize: `${translationFontSize}px`, color: 'oklch(0.62 0.02 80)' }}>
                      {ayah.translations[0].text}
                    </p>
                  )}
                  <div className="mt-auto flex flex-col items-center gap-1">
                    <p className="text-[10px] tracking-[0.18em] uppercase" style={{ color: 'oklch(0.45 0.02 80)' }}>
                      {surahName ? `${surahName} · ${ayah.surahNumber}:${ayah.numberInSurah}` : `${ayah.surahNumber}:${ayah.numberInSurah}`}
                    </p>
                    <p className="text-[9px] tracking-widest uppercase font-light" style={{ color: 'oklch(0.32 0.01 80)' }}>ramadanlifecenter.com</p>
                  </div>
                </div>
              </div>
              </CarouselItem>
            </CarouselContent>
          </Carousel>

          {/* Carousel Indicators */}
          <div className="flex gap-1.5 justify-center mt-3">
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <div
                key={index}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  currentSlide === index ? 'w-4 bg-primary' : 'w-1.5 bg-primary/40'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Floating back button — top left */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => navigate(-1)}
        className="absolute top-4 left-4 z-30 h-9 w-9 rounded-full bg-black/40 backdrop-blur-md text-white hover:bg-black/60 hover:text-white border border-white/10"
      >
        <ArrowLeft className="w-4 h-4" />
      </Button>



      {/* Floating toolbar — right side */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2 z-30 flex flex-col gap-2 w-[52px]">
        {/* Settings controls */}
        <div className="backdrop-blur-xl bg-black/50 border border-white/15 rounded-2xl p-2 flex flex-col items-center gap-3">
          {/* Arabic toggle */}
          <button
            onClick={() => setShowArabic(!showArabic)}
            className={`w-9 h-9 rounded-xl flex items-center justify-center text-[10px] font-bold transition-all ${
              showArabic ? 'bg-white/20 text-white' : 'bg-transparent text-white/40'
            }`}
            title="แสดงอาหรับ"
          >
            عر
          </button>

          {/* Divider */}
          <div className="w-6 h-px bg-white/15" />

          {/* Arabic font size — vertical slider */}
          {showArabic && (
            <>
              <span className="text-[9px] text-white/60 tabular-nums">{fontSize}</span>
              <div className="h-20 flex items-center justify-center">
                <Slider
                  orientation="vertical"
                  min={8}
                  max={60}
                  step={1}
                  value={[fontSize]}
                  onValueChange={([v]) => setFontSize(v)}
                  className="h-full"
                />
              </div>
              <span className="text-[8px] text-white/40 uppercase tracking-wider">ع</span>
              <div className="w-6 h-px bg-white/15" />
            </>
          )}

          {/* Translation font size — vertical slider */}
          <span className="text-[9px] text-white/60 tabular-nums">{translationFontSize}</span>
          <div className="h-20 flex items-center justify-center">
            <Slider
              orientation="vertical"
              min={8}
              max={32}
              step={1}
              value={[translationFontSize]}
              onValueChange={([v]) => setTranslationFontSize(v)}
              className="h-full"
            />
          </div>
          <span className="text-[8px] text-white/40 uppercase tracking-wider">Aa</span>
        </div>

        {/* Action buttons */}
        <div className="backdrop-blur-xl bg-black/50 border border-white/15 rounded-2xl p-2 flex flex-col items-center gap-2">
          <button
            onClick={handleWebShare}
            className="w-9 h-9 rounded-xl bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition-all"
            title="แชร์"
          >
            <Share2 className="w-4 h-4" />
          </button>
          <button
            onClick={handleCopyImage}
            disabled={isCopyingImage}
            className="w-9 h-9 rounded-xl bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition-all disabled:opacity-40"
            title="คัดลอกรูป"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
          </button>
        </div>
      </div>
    </div>
  );
}

export default OneDayOneAyahSharePage;
